import Text from '@components/Text';
import colors from '@constants/colors';
import styled from '@emotion/styled';
import Backspace from '@icons/Backspace';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

const MAX_LENGTH = 6;

interface Props {
    pin: string;
    /** 키를 누른 뒤의 값을 넘겨준다 (최대 6자리) */
    onPinChange: (pin: string) => void;
    /** 값이 바뀌면 숫자 배열을 다시 섞는다 */
    shuffleKey?: unknown;
}

/** 숫자 위치가 매번 섞이는 PIN 입력 키패드 */
function PinKeyboard({ pin, onPinChange, shuffleKey }: Props) {
    const { t } = useTranslation('easyCard');
    const { nums, last } = useMemo(() => {
        const arr = Array.from({ length: 10 }).map((_, idx) => idx);
        const sorted = arr.sort(() => (Math.round(Math.random() * 10) % 2 === 0 ? -1 : 1));
        const popped = sorted.pop();

        return {
            nums: sorted,
            last: popped!,
        };
    }, [shuffleKey]);

    const addToPin = (num: number) => {
        if (pin.length < MAX_LENGTH) {
            onPinChange(`${pin}${num}`);
        }
    };

    return (
        <Container>
            {nums.map((num) => (
                <KeyPad key={`button-${num}`}
                    onClick={() => addToPin(num)} >
                    <Text size='display' fontWeight='bold' color={colors.buttonText}>
                        {num}
                    </Text>
                </KeyPad>
            ))}
            <KeyPad onClick={() => onPinChange('')}>
                <Text size='title' fontWeight='bold' color={colors.buttonText}>
                    {t('pin.clearAll')}
                </Text>
            </KeyPad>
            <KeyPad onClick={() => addToPin(last)}>
                <Text size='display' fontWeight='bold' color={colors.buttonText}>
                    {last}
                </Text>
            </KeyPad>
            <KeyPad onClick={() => onPinChange(pin.slice(0, -1))}>
                <Backspace size={30} color={colors.buttonText} />
            </KeyPad>
        </Container>
    );
}

const Container = styled.div`
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    padding-bottom: var(--safe-area-bottom);
    background-color: var(--color-primary);
    width: 100%;
`;

const KeyPad = styled.button`
    width: 100%;
    display: flex;
    justify-content: center;
    align-items: center;
    height: 70px;
    background-color: transparent;
`;

export default PinKeyboard;
